<template>
  <div v-if="show" class="rh-screen">
    <p class="rh-instruction">{{ translation.instruction }}</p>

    <div class="rh-board-area">
      <!-- Exit arrow -->
      <div class="rh-exit-arrow" :style="exitArrowStyle">▶</div>

      <div
        class="rh-board"
        ref="board"
        @pointermove.prevent="onMove"
        @pointerup="onUp"
        @pointercancel="onUp"
      >
        <!-- 36 background cells -->
        <div v-for="i in 36" :key="i" class="rh-cell" />

        <!-- Vehicles -->
        <div
          v-for="v in vehicles"
          :key="v.id"
          :class="['rh-vehicle', { 'rh-target-car': v.isTarget, 'rh-truck': v.size === 3 }]"
          :style="vehicleStyle(v)"
          @pointerdown.prevent="onDown($event, v)"
        >
          <span v-if="v.isTarget" class="rh-car-arrow">▶</span>
        </div>
      </div>
    </div>

    <!-- Bravo overlay after exit -->
    <transition name="rh-bravo">
      <div v-if="won" class="rh-bravo-overlay">
        <div class="rh-bravo-card">
          <div class="rh-bravo-emoji">🚗💨</div>
          <p class="rh-bravo-text">{{ translation.bravo }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'RushHour',
  props: { pointData: { type: Object, default: null } },
  emits: ['rh-done'],

  data() {
    return {
      vehicles:  [],
      drag:      null,   // { vehicleId, horizontal, startPX, startPY, startCol, startRow, cellPx }
      exitingId: null,
      won:       false,
      translation: { instruction: 'Libérez la voiture rouge !', bravo: 'Bravo !' },
    };
  },

  computed: {
    show() { return this.$parent.showRushHour === true; },
    targetVehicle() { return this.vehicles.find(v => v.isTarget); },
    exitArrowStyle() {
      const row = this.targetVehicle?.row ?? 2;
      const U = 100 / 6;
      return { top: `calc(${row * U + U / 2}% - 14px)` };
    },
  },

  watch: {
    'pointData.rushHourVehicles': { immediate: true, handler(val) { this.init(val); } },
    show(val) { if (val) this.init(this.pointData?.rushHourVehicles); },
  },

  mounted() {
    this.translate(this.$store.getters.lang);
  },

  methods: {
    init(raw) {
      this.won       = false;
      this.exitingId = null;
      this.drag      = null;
      if (!raw) { this.vehicles = []; return; }
      const arr = Array.isArray(raw) ? raw : Object.values(raw);
      this.vehicles = arr.map(v => ({ ...v }));
    },

    // ── Board helpers ────────────────────────────────────────────
    buildOccupancy(excludeId = null) {
      const g = Array.from({ length: 6 }, () => Array(6).fill(null));
      for (const v of this.vehicles) {
        if (v.id === excludeId) continue;
        for (let i = 0; i < v.size; i++) {
          const r = v.horizontal ? v.row : v.row + i;
          const c = v.horizontal ? v.col + i : v.col;
          if (r >= 0 && r < 6 && c >= 0 && c < 6) g[r][c] = v.id;
        }
      }
      return g;
    },

    canPlace(v, row, col, occ) {
      for (let i = 0; i < v.size; i++) {
        const r = v.horizontal ? row : row + i;
        const c = v.horizontal ? col + i : col;
        if (r < 0 || r > 5 || c < 0 || c > 5) return false;
        if (occ[r][c] !== null) return false;
      }
      return true;
    },

    // ── Styles ───────────────────────────────────────────────────
    vehicleStyle(v) {
      const U  = 100 / 6;
      const G  = 0.8;
      const isExiting  = this.exitingId === v.id;
      const isDragging = this.drag?.vehicleId === v.id;

      const left   = isExiting ? 115 : v.col * U + G;
      const top    = v.row * U + G;
      const width  = v.horizontal ? v.size * U - G * 2 : U - G * 2;
      const height = v.horizontal ? U - G * 2 : v.size * U - G * 2;
      const color  = v.isTarget ? '#e53935' : (v.color || '#1e88e5');

      return {
        left:       left + '%',
        top:        top  + '%',
        width:      width  + '%',
        height:     height + '%',
        background: color,
        transition: isExiting ? 'left 0.55s ease-in' : 'none',
        zIndex:     isDragging ? 10 : 2,
        cursor:     this.exitingId ? 'default' : (isDragging ? 'grabbing' : 'grab'),
      };
    },

    // ── Drag ─────────────────────────────────────────────────────
    onDown(e, v) {
      if (this.exitingId !== null || this.won) return;
      const rect   = this.$refs.board.getBoundingClientRect();
      const cellPx = rect.width / 6;
      this.drag = {
        vehicleId: v.id,
        horizontal: v.horizontal,
        startPX: e.clientX,
        startPY: e.clientY,
        startCol: v.col,
        startRow: v.row,
        cellPx,
      };
      this.$refs.board.setPointerCapture(e.pointerId);
    },

    onMove(e) {
      if (!this.drag) return;
      const { vehicleId, horizontal, startPX, startPY, startCol, startRow, cellPx } = this.drag;
      const v = this.vehicles.find(x => x.id === vehicleId);
      if (!v) return;

      const occ = this.buildOccupancy(vehicleId);

      if (horizontal) {
        const deltaCols = Math.round((e.clientX - startPX) / cellPx);
        const newCol    = Math.max(0, Math.min(6 - v.size, startCol + deltaCols));
        if (this.canPlace(v, v.row, newCol, occ)) {
          v.col = newCol;
          if (v.isTarget && v.col + v.size >= 6) {
            this.triggerExit(v);
          }
        }
      } else {
        const deltaRows = Math.round((e.clientY - startPY) / cellPx);
        const newRow    = Math.max(0, Math.min(6 - v.size, startRow + deltaRows));
        if (this.canPlace(v, newRow, v.col, occ)) {
          v.row = newRow;
        }
      }
    },

    onUp() {
      this.drag = null;
    },

    // ── Exit & win ───────────────────────────────────────────────
    triggerExit(v) {
      this.drag = null;
      this.exitingId = v.id;
      // Apply transition class first, then move off-board in next tick
      this.$nextTick(() => {
        v.col = 7;
        new Audio(require('../../static/sounds/correctAnswer.mp3')).play().catch(() => {});
        this.$parent.errorNumber += 3;
        this.$parent.showStarUp('3+');
        localStorage.setItem('errorNumber', this.$parent.errorNumber);

        setTimeout(() => {
          this.won = true;
          setTimeout(() => { this.$emit('rh-done'); }, 1600);
        }, 600);
      });
    },

    // ── Translations ─────────────────────────────────────────────
    translate(lang) {
      const map = {
        fr: { instruction: 'Faites glisser les voitures pour libérer la rouge !',   bravo: 'Bravo ! Puzzle résolu ! 🎉' },
        he: { instruction: 'הזיזו את המכוניות כדי לשחרר את האדומה!',              bravo: '!כל הכבוד! פתרת את הפאזל 🎉' },
        en: { instruction: 'Slide the cars to free the red one!',                   bravo: 'Well done! Puzzle solved! 🎉' },
        de: { instruction: 'Schiebe die Autos, um das rote zu befreien!',           bravo: 'Super! Rätsel gelöst! 🎉' },
        ar: { instruction: 'حرّك السيارات لتحرير السيارة الحمراء!',                bravo: '!أحسنت! حللت اللغز 🎉' },
      };
      this.translation = map[lang] || map.fr;
    },
  },
};
</script>

<style scoped>
.rh-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 12px 80px;
  gap: 16px;
  width: 100%;
}

.rh-instruction {
  font-family: 'VarelaRound', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  text-align: center;
  margin: 0;
  text-shadow: 0 1px 4px rgba(0,0,0,0.5);
}

/* ── Board ──────────────────────────────────────────────── */
.rh-board-area {
  position: relative;
  width: 100%;
  max-width: 360px;
}

.rh-exit-arrow {
  position: absolute;
  right: -28px;
  color: #ffeb3b;
  font-size: 22px;
  font-weight: 900;
  filter: drop-shadow(0 0 8px rgba(255,235,59,0.9));
  pointer-events: none;
  z-index: 10;
}

.rh-board {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background: #2a3d2a;
  border: 7px solid #4e342e;
  border-radius: 6px;
  overflow: hidden;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: repeat(6, 1fr);
  touch-action: none;
  box-shadow:
    inset 0 0 30px rgba(0,0,0,0.4),
    0 6px 24px rgba(0,0,0,0.5);
}

.rh-cell {
  border: 0.5px solid rgba(255,255,255,0.07);
  box-sizing: border-box;
}

/* ── Vehicles ───────────────────────────────────────────── */
.rh-vehicle {
  position: absolute;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    inset 0 3px 0 rgba(255,255,255,0.28),
    inset 0 -2px 0 rgba(0,0,0,0.3),
    0 4px 10px rgba(0,0,0,0.45);
  user-select: none;
  touch-action: none;
}

.rh-target-car {
  box-shadow:
    inset 0 3px 0 rgba(255,255,255,0.3),
    inset 0 -2px 0 rgba(0,0,0,0.35),
    0 4px 10px rgba(0,0,0,0.45),
    0 0 16px rgba(229,57,53,0.55);
}

.rh-car-arrow {
  font-size: 18px;
  color: rgba(255,255,255,0.85);
  text-shadow: 0 1px 3px rgba(0,0,0,0.5);
  pointer-events: none;
}

/* ── Bravo overlay ──────────────────────────────────────── */
.rh-bravo-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  backdrop-filter: blur(3px);
}

.rh-bravo-card {
  background: linear-gradient(135deg, #1b5e20, #2e7d32);
  border: 2px solid rgba(255,255,255,0.2);
  border-radius: 24px;
  padding: 36px 40px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
}

.rh-bravo-emoji {
  font-size: 52px;
  line-height: 1;
  margin-bottom: 14px;
}

.rh-bravo-text {
  font-family: 'VarelaRound', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #fff;
  margin: 0;
}

/* ── Transitions ────────────────────────────────────────── */
.rh-bravo-enter-active { animation: rh-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes rh-pop {
  from { opacity: 0; transform: scale(0.6); }
  to   { opacity: 1; transform: scale(1); }
}
</style>
